// @flow
import React from "react";
import shallowCompare from "react-addons-shallow-compare";
import { connect } from "react-redux";
import Sticky from "react-stickynode";
import Waypoint from "react-waypoint";
import BookmarkButtonAlt from "backpack-ui/dist/components/bookmarkButtonAlt";
import capitalize from "lodash/capitalize";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import PageHeader from "./pageHeader";
import EssentialInfo from "./essentialInfo";
import ContentHeader from "./contentHeader";
import ContentSectionList from "./contentSectionList";
import { connected as NearbyPlaces } from "./nearbyPlaces";
import ImagesContainer from "./imagesContainer";
import ShareMenu from "./shareMenu";
import ContactBar from "./contactBar";
import Availability from "./availability";
import TourItinerary from "./tourItinerary";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import styles from "./styles";
import { getPartnerActivityType } from "../utils/createPartnerActivity";
import { createListUrl, createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import analytics from "../../shared/actions/analytics";
import fullItineraryLink from "../utils/fullItineraryLink";
import DataLayer from "../../shared/components/dataLayer";
import createUnitLabel from "../utils/createUnitLabel";
import cleanUp from "../utils/cleanUp";
import {
  getPartnerActivityPrice,
  getPartnerActivityDiscountedPrice,
  getPartnerActivityCurrency,
} from "../utils/partnerActivityPricing";
import partnerActivityProviders from "../../../../server/lib/open-planet/partnerActivityProviders";
import type { Activity, Place, Breadcrumb, WaypointLeaveEvent, MobileState } from "../../../../server/types";

const _ = { capitalize };

export type ActivityPoiComponentProps = {
  poi: Activity,
  place: Place,
  images: Array<string>,
  breadcrumb: Array<Breadcrumb>,
  mobile: MobileState,
  params: Object,
  dispatch: Function,
  onClickBookmark: Function,
  marked: Boolean,
};
/**
 * Renders a bookable activity POI
 */
class ActivityPoiComponent extends React.Component {
  constructor() {
    super();

    this.state = {
      nearby: false,
      hideMap: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.enterNearbyPlaces = this.enterNearbyPlaces.bind(this);
    this.leaveNearbyPlaces = this.leaveNearbyPlaces.bind(this);
  }

  state: {
    nearby: boolean,
    hideMap: boolean,
  };

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl: () => string;

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(poi, breadcrumb.map(b => b.title), params.placeId);
  }

  props: ActivityPoiComponentProps;
  enterNearbyPlaces: () => void;
  leaveNearbyPlaces: (event: WaypointLeaveEvent) => void;

  enterNearbyPlaces() {
    this.setState({
      hideMap: true,
    });
  }

  leaveNearbyPlaces(event: WaypointLeaveEvent) {
    this.setState({
      hideMap: (event.currentPosition === "above"),
    });
  }

  render() {
    const { poi, breadcrumb, images, place, dispatch, params } = this.props;

    const subtype = getPartnerActivityType(poi.duration);
    const subtypeSingular = getPartnerActivityType(poi.duration, "singular");
    const resource = subtype.indexOf("tour") > -1 ? "tours" : "activities";

    const tinyHeading = {
      level: 4,
      size: "tiny",
      weight: "thick",
      importance: "high",
      caps: true,
    };

    const smallHeading = {
      level: 4,
      size: "small",
      weight: "thick",
      override: {
        fontSize: "20px",
        lineHeight: (32 / 20),
      },
    };

    const mediumHeading = {
      level: 3,
      size: "medium",
      weight: "thin",
    };

    const name = poi.name;

    const poiId = `poi-${poi.id}`;

    const categoryLink = createListUrl({
      resource: poi.type,
      kind: resource,
      breadcrumb: breadcrumb.map(b => b.title),
      placeId: place.id,
    });

    const currentBreadcrumb = {
      title: _.capitalize(subtype),
      href: categoryLink,
    };

    const description = () => {
      if (poi.descriptions.short_description) {
        return `<p>${cleanUp.replaceNewlineWithParagraph(poi.descriptions.short_description)}</p>
      <p>${cleanUp.replaceNewlineWithParagraph(poi.descriptions.long_description)}</p>`;
      }
      return cleanUp.replaceNewlineWithParagraph(poi.descriptions.long_description);
    };

    const { duration, unit } = poi.duration;

    const hasStrapline = subtype === "tours" &&
      poi.provider === partnerActivityProviders.gadventures.id &&
      poi.duration &&
      duration &&
      unit &&
      typeof poi.departureInfo === "string" &&
      typeof poi.returnInfo === "string";

    const strapline = hasStrapline ?
      `${duration} ${createUnitLabel(duration, unit)}, ${poi.departureInfo} to ${poi.returnInfo}` :
      "";

    const providerName = poi.provider ?
      partnerActivityProviders[poi.provider].name :
      "";

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <DataLayer
          data={{
            cd26_PartnerOfferingDetails: name,
            cd27_PartnerOfferingKind: subtypeSingular,
            cd28_PartnerName: providerName,
          }}
        />
        <Breadcrumbs
          links={[...breadcrumb, currentBreadcrumb]}
        />

        <div className="Page Page--detail">
          <PageHeader
            heading={name}
            title={`${place.name} ${subtype}`}
            titleHref={categoryLink}
            topChoice={poi.poiAttributes && poi.poiAttributes.length &&
              poi.poiAttributes[0].name === "top_choice"}
            type={poi.categories[0]}
            place={place.name}
            alignment="center"
            strapline={strapline}
            contained
          />

          <div className="PageContent">
            {this.props.marked !== null &&
              <div>
                <BookmarkButtonAlt
                  className="js-gtm-bookmark-button"
                  onClick={this.props.onClickBookmark}
                  marked={this.props.marked}
                  style={styles.bookmarkButtonAlt}
                />
                <FeatureDiscover
                  featureName="firstPoiSeen"
                  removeOnOuterClick
                  removeOnNavigate
                >
                  <DiscoveryFlyout
                    headline="Save this place"
                    description="Tap the bookmark to save and organize your interests into lists."
                    style={styles.featureDiscovery.button}
                  />
                </FeatureDiscover>
              </div>
            }

            {images && images.length > 0 && this.props.mobile &&
              <ImagesContainer images={images} mobile />
            }

            <aside className="PageAside" data-hide-map={this.state.hideMap}>
              <Sticky
                enabled={!this.props.mobile}
                top={30}
                bottomBoundary={`#${poiId} .PageContent`}
              >
                {!this.props.mobile &&
                  <Availability
                    price={{
                      amount: getPartnerActivityPrice(poi),
                      currency: getPartnerActivityCurrency(poi),
                      discountedAmount: getPartnerActivityDiscountedPrice(poi),
                      poiType: "partnerActivity",
                    }}
                    url={poi.links.booking}
                    buttonClick={() => dispatch(analytics.partnerClick({
                      type: subtypeSingular,
                      provider: providerName,
                      name,
                    }))}
                  />
                }

                {!this.props.mobile &&
                  <EssentialInfo
                    poi={poi}
                    mobile={this.props.mobile}
                    links={breadcrumb}
                  />
                }

                {(poi.telephone || poi.url) &&
                  <ContactBar
                    phone={poi.telephone}
                    website={poi.url}
                  />
                }

                {this.props.mobile &&
                  <Availability
                    price={{
                      amount: getPartnerActivityPrice(poi),
                      currency: getPartnerActivityCurrency(poi),
                      discountedAmount: getPartnerActivityDiscountedPrice(poi),
                      poiType: "partnerActivity",
                    }}
                    url={poi.links.booking}
                    buttonClick={() => dispatch(analytics.partnerClick({
                      type: subtypeSingular,
                      provider: providerName,
                      name,
                    }))}
                  />
                }

                {!this.props.mobile && poi.links.map &&
                  <div>
                    <img src={poi.links.map} alt={`Map of ${poi.name}`} />
                  </div>
                }
              </Sticky>
            </aside>

            <article className="PageArticle">
              {images && images.length > 0 && !this.props.mobile &&
                <ImagesContainer images={images} />
              }

              <div className="PageArticle-content">
                <article>
                  {poi.descriptions &&
                    <ContentBlock
                      heading={`Tour description provided by ${providerName}`}
                      htmlContent={description()}
                      type="partnerDescription"
                    />
                  }

                  {poi.itinerary.length > 0 &&
                    <section className="ContentSection">
                      <ContentHeader
                        title="Itinerary"
                        heading={mediumHeading}
                        border="bottom"
                      />

                      <TourItinerary
                        itinerary={poi.itinerary}
                        link={fullItineraryLink(poi)}
                      />
                    </section>
                  }

                  <footer className="ContentFooter">
                    <div className="ContentFooter-share">
                      <ShareMenu
                        text={poi.name}
                        url={`https://www.lonelyplanet.com${this.getPoiUrl()}`}
                        mobile={this.props.mobile}
                      />
                    </div>
                  </footer>
                </article>

                <aside style={{ marginTop: "73px" }}>
                  {poi.inclusions.length > 0 &&
                    <ContentSectionList
                      header={{
                        title: "What’s included",
                        heading: mediumHeading,
                        border: "bottom",
                      }}
                      listItems={poi.inclusions}
                    />
                  }

                  {poi.exclusions.length > 0 &&
                    <ContentSectionList
                      header={{
                        title: "What’s not included",
                        heading: mediumHeading,
                        border: "bottom",
                      }}
                      listItems={poi.exclusions}
                    />
                  }

                  {subtype === "activities" && poi.highlights.length > 0 &&
                    <ContentSectionList
                      header={{
                        title: "Highlights",
                        heading: mediumHeading,
                        border: "bottom",
                      }}
                      listItems={poi.highlights}
                    />
                  }

                  {poi.accommodation &&
                    <ContentSectionList
                      header={{
                        title: "Accommodations",
                        heading: smallHeading,
                      }}
                      listItems={poi.accommodation}
                    />
                  }

                  {poi.transportation &&
                    <ContentSectionList
                      header={{
                        title: "Transportation",
                        heading: smallHeading,
                      }}
                      listItems={poi.transportation}
                    />
                  }

                  {poi.meals &&
                    <ContentSectionList
                      header={{
                        title: "Meals",
                        heading: smallHeading,
                      }}
                      listItems={poi.meals}
                    />
                  }

                  {poi.staffExperts &&
                    <ContentSectionList
                      header={{
                        title: "Staff & experts",
                        heading: smallHeading,
                      }}
                      listItems={poi.staffExperts}
                    />
                  }

                  {poi.location &&
                    <section className="ContentSection ContentSection-nearbyPlaces">
                      <ContentHeader
                        title="Nearby & Noteworthy"
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <NearbyPlaces
                        currentPlace={poi}
                        mapId={`leaflet-map-container-${poi.id}`}
                        relatedPlaceId={params.placeId}
                      />

                      <Waypoint
                        onEnter={this.enterNearbyPlaces}
                        onLeave={this.leaveNearbyPlaces}
                        fireOnRapidScroll
                      />
                    </section>
                  }
                </aside>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

ActivityPoiComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: React.PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: React.PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: React.PropTypes.array.isRequired,

  /**
   * The breadcrumbs for this component
   */
  breadcrumb: React.PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: React.PropTypes.bool,

  /**
   * React router params
   */
  params: React.PropTypes.object,

  /**
   * Store dispatch method from connect
   */
  dispatch: React.PropTypes.func,
  onClickBookmark: React.PropTypes.func,
  marked: React.PropTypes.bool,
};

const mapStateToProps = (state) => ({
  mobile: state.mobile.isMobile,
});

export default connect(mapStateToProps)(ActivityPoiComponent);



// WEBPACK FOOTER //
// ./assets/pois/components/activity.jsx